<template>
  <Teleport to="body">
    <div v-if="isVisible" class="captcha-popup-overlay" @click.self="handleCancel">
      <div class="captcha-popup-content">
        <button class="captcha-popup-close" @click="handleCancel">
          <i class="zmdi zmdi-close" />
        </button>
        
        <h3 class="captcha-popup-title">{{ $t('auth.captchaPopupTitle') }}</h3>
        <p class="captcha-popup-subtitle">{{ $t('auth.captchaPopupSubtitle') }}</p>
        
        <div class="captcha-popup-widget">
          <div :id="containerId" />
        </div>
        
        <p v-if="error" class="captcha-popup-error">{{ error }}</p>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { 
  getPopupVisibility, 
  completeCaptchaVerification, 
  cancelCaptchaVerification 
} from '@/shared/utils/captchaRetryService'
import { getCaptchaProvider } from '@/shared/utils/captchaConfig'
import { 
  loadRecaptcha, 
  renderRecaptchaWidget, 
  resetRecaptchaWidget 
} from '@/shared/utils/recaptcha'
import {
  loadTurnstile,
  renderTurnstileWidget,
  resetTurnstileWidget,
  executeTurnstileWidget
} from '@/shared/utils/turnstile'
import {
  executeRecaptchaWidget
} from '@/shared/utils/recaptcha'
import {
  initGeetestWidget,
  serializeGeetestResult,
  type GeetestCaptchaObject
} from '@/shared/utils/geetest'

const isVisible = getPopupVisibility()
const containerId = 'captcha-popup-widget'
const widgetId = ref<number | string | null>(null)
const geetestObj = ref<GeetestCaptchaObject | null>(null)
const error = ref('')
const provider = getCaptchaProvider()

const { t } = useI18n()

// Retry logic for GeeTest network errors
const MAX_GEETEST_RETRIES = 3
const GEETEST_RETRY_DELAY_MS = 1000
const geetestRetryCount = ref(0)

const handleCancel = () => {
  geetestRetryCount.value = 0 // Reset retries on cancel
  cancelCaptchaVerification()
}

const onCaptchaSuccess = (token: string) => {
  geetestRetryCount.value = 0 // Reset retries on success
  completeCaptchaVerification(token)
}

const retryGeetestCaptcha = async () => {
  if (!geetestObj.value) return
  
  console.log(`[GonkaGame] Retrying GeeTest captcha (attempt ${geetestRetryCount.value}/${MAX_GEETEST_RETRIES})`)
  
  // Wait before retry
  await new Promise(resolve => setTimeout(resolve, GEETEST_RETRY_DELAY_MS))
  
  // Reset and re-show
  geetestObj.value.reset()
  geetestObj.value.showCaptcha()
}

const onCaptchaError = (captchaError?: any) => {
  // Check if this is a GeeTest network error and we can retry
  const isGeetestNetworkError = provider === 'geetest' && (
    captchaError?.message?.includes('网络错误') ||  // Chinese "Network error"
    captchaError?.msg?.includes('网络错误') ||
    String(captchaError).includes('网络错误')
  )
  
  if (isGeetestNetworkError && geetestRetryCount.value < MAX_GEETEST_RETRIES) {
    geetestRetryCount.value++
    retryGeetestCaptcha()
    return // Don't show error, we're retrying
  }
  
  // Reset retry counter after max retries or non-network error
  geetestRetryCount.value = 0
  
  if (provider === 'geetest') {
    error.value = t('auth.captchaGeetestError')
  } else if (provider === 'turnstile') {
    error.value = t('auth.captchaTurnstileError')
  } else {
    error.value = t('auth.captchaError')
  }
}

const onCaptchaExpired = () => {
  error.value = t('auth.captchaExpired')
}

const initWidget = async () => {
  try {
    // Small delay to ensure container is in DOM
    await new Promise(resolve => setTimeout(resolve, 100))
    
    if (provider === 'geetest') {
      geetestObj.value = await initGeetestWidget(containerId, {
        onReady: () => {
          // Auto-show challenge immediately when ready
          if (geetestObj.value) {
            geetestObj.value.showCaptcha()
          }
        },
        onSuccess: (result) => {
          onCaptchaSuccess(serializeGeetestResult(result))
        },
        onError: onCaptchaError,
      })
    } else if (provider === 'turnstile') {
      await loadTurnstile()
      widgetId.value = await renderTurnstileWidget(containerId, {
        onSuccess: onCaptchaSuccess,
        onError: onCaptchaError,
        onExpired: onCaptchaExpired,
      })
      executeTurnstileWidget(widgetId.value as string)
    } else {
      await loadRecaptcha()
      widgetId.value = await renderRecaptchaWidget(containerId, {
        onSuccess: onCaptchaSuccess,
        onError: onCaptchaError,
        onExpired: onCaptchaExpired,
      })
      executeRecaptchaWidget(widgetId.value as number)
    }
  } catch (err) {
    console.error('[GonkaGame] Captcha popup init error:', err)
    error.value = 'Не удалось загрузить проверку'
  }
}

watch(isVisible, async (visible) => {
  if (visible) {
    error.value = ''

    // Dev mode: auto-complete captcha immediately
    if (provider === 'none') {
      onCaptchaSuccess('dev-bypass')
      return
    }

    // Reset existing widget or create new one
    if (provider === 'geetest' && geetestObj.value) {
      geetestObj.value.reset()
    } else if (widgetId.value !== null) {
      if (provider === 'turnstile') {
        resetTurnstileWidget(widgetId.value as string)
        executeTurnstileWidget(widgetId.value as string)
      } else {
        resetRecaptchaWidget(widgetId.value as number)
        executeRecaptchaWidget(widgetId.value as number)
      }
    } else {
      await initWidget()
    }
  }
})
</script>

<style scoped>
.captcha-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--ds-scrim);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.captcha-popup-content {
  background: var(--hm-surface);
  border-radius: var(--ds-radius-card-lg);
  padding: 32px;
  max-width: 400px;
  width: 90%;
  position: relative;
  box-shadow: var(--hm-shadow-soft);
}

.captcha-popup-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: var(--ds-text-size-h-md);
  color: var(--hm-text-muted);
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  
  &:hover {
    color: var(--hm-text-primary);
  }
}

.captcha-popup-title {
  font-size: var(--ds-text-size-h-sm);
  font-weight: var(--ds-weight-semibold);
  text-align: center;
  margin-bottom: 8px;
  color: var(--hm-text-primary);
}

.captcha-popup-subtitle {
  font-size: var(--ds-text-size-body);
  color: var(--hm-text-secondary);
  text-align: center;
  margin-bottom: 24px;
}

.captcha-popup-widget {
  display: flex;
  justify-content: center;
  min-height: 78px;
}

.captcha-popup-error {
  color: var(--hm-danger);
  font-size: var(--ds-text-size-body);
  text-align: center;
  margin-top: 16px;
}
</style>
